import { appsForExt } from '@/apps/registry';
import { associationFor } from '@/config/fileAssociations';
import { confirmDelete, folderBehind } from '@/dnd/drop';
import { canDelete } from '@/fs/mutate';
import { fsApi } from '@/fs/useFs';
import { isRecycleBinNode, useRecycleStore } from '@/recycle/store';
import { separator } from '@/shell/Menus/types';
import { useWindowStore } from '@/wm/store';
import type { FsNode } from '@/fs/types';
import type { MenuItem } from '@/shell/Menus/types';
import type { AppArgs } from '@/wm/types';
import {
  DEFAULT_VERB,
  defaultVerbItem,
  describe,
  msgbox,
  newMenu,
  readOnlyDisk,
  searchCompanion,
  sendToMenu,
  showProperties,
} from './common';

/**
 * The menus a file, a folder and the empty space between them carry.
 *
 * One set for the whole shell: a text file has the same menu whether it is
 * sitting on the desktop, in a folder window, or in the Recycle Bin's list of
 * things it used to be. What changes between those places is only what Open
 * means and whether Explore is on offer, which is what `FileMenuOptions` is
 * for.
 */

export interface FileMenuOptions {
  /** The default verb. Defaults to launching the file's associated app. */
  onOpen?(node: FsNode): void;
  /**
   * Opens a folder in a second window with the folder tree showing. Takes a
   * path rather than the node, because the folder a shortcut stands for is not
   * the shortcut.
   */
  onExplore?(path: string): void;
  /** Everything the command should act on; defaults to the node alone. */
  selection?: readonly FsNode[];
}

/**
 * Open With ▸, from the same table the double-click consults.
 *
 * Only files get one, and only files something claims — XP offered the
 * submenu for anything, but a list whose every row is "Choose Program..." is a
 * menu about nothing.
 */
function openWithMenu(node: FsNode): MenuItem | null {
  if (node.type !== 'file') return null;

  const candidates = appsForExt(node.ext);
  if (candidates.length === 0) return null;

  // Which argument carries the file is the association's business, the same as
  // it is for a double-click — with two exceptions that do not hand over a
  // path at all: an internet shortcut, where the browser wants the address
  // inside the file, and a sound file, where the players take a track and,
  // this being an activation like any other, start it.
  const args: AppArgs =
    node.data.kind === 'url'
      ? { url: node.data.href }
      : node.data.kind === 'audio'
        ? { track: node.data.trackId, autoplay: true }
        : { [associationFor(node.ext)?.argKey ?? 'file']: node.path };

  return {
    label: 'Open Wit&h',
    items: [
      ...candidates.map<MenuItem>((app) => ({
        label: app.name,
        icon: app.icon,
        onSelect: () => {
          useWindowStore.getState().open(app.id, args);
        },
      })),
      separator,
      { label: 'Choose &Program...', disabled: true },
    ],
  };
}

/**
 * The Recycle Bin's own menu. It has no Delete and no Rename — you could not
 * put the Recycle Bin in the Recycle Bin — and gains Empty, greyed when there
 * is nothing to empty, exactly as the real one was.
 */
function recycleBinMenu(node: FsNode, options: FileMenuOptions): MenuItem[] {
  const { items, empty } = useRecycleStore.getState();
  const open = options.onOpen ?? ((target: FsNode) => fsApi.openFile(target));

  return [
    defaultVerbItem(DEFAULT_VERB, () => open(node)),
    {
      label: '&Explore',
      disabled: options.onExplore === undefined,
      onSelect: options.onExplore ? () => options.onExplore?.(node.path) : undefined,
    },
    separator,
    {
      label: 'E&mpty Recycle Bin',
      disabled: items.length === 0,
      // Word for word what the bin's own Empty asks — it is one shell command
      // with two entrances, and XP named a lone item rather than counting it.
      onSelect: () =>
        msgbox({
          title: items.length === 1 ? 'Confirm File Delete' : 'Confirm Multiple File Delete',
          icon: 'question',
          text:
            items.length === 1
              ? `Are you sure you want to delete ${describe(items.map((entry) => entry.node))}?`
              : `Are you sure you want to delete these ${items.length} items?`,
          buttons: ['Yes', 'No'],
          onResult: (button: string) => {
            if (button === 'Yes') empty();
          },
        }),
    },
    separator,
    { label: 'Create &Shortcut', onSelect: () => readOnlyDisk('shortcut') },
    separator,
    { label: 'P&roperties', onSelect: () => showProperties(node) },
  ];
}

/**
 * One file or folder.
 *
 * `selection` is everything the command should act on, which is the whole
 * selection when the right-click landed inside one and just this node when it
 * did not — the distinction Delete and Send To are the only items here to care
 * about.
 */
export function fileMenu(node: FsNode, options: FileMenuOptions = {}): MenuItem[] {
  if (isRecycleBinNode(node)) return recycleBinMenu(node, options);

  const selection = options.selection?.length ? options.selection : [node];
  const open = options.onOpen ?? ((target: FsNode) => fsApi.openFile(target));
  const openWith = openWithMenu(node);

  const verbs: MenuItem[] = [defaultVerbItem(DEFAULT_VERB, () => open(node))];

  // A shortcut is whatever it points at: My Documents on the desktop is a
  // `.lnk`, and it opens, explores and searches like the folder it stands for.
  const folder = folderBehind(node);

  if (folder) {
    // Explore opened the same place with the folder tree showing, which is all
    // that ever distinguished it from Open. With nowhere to open a second
    // window the row still belongs — greyed.
    verbs.push({
      label: '&Explore',
      disabled: options.onExplore === undefined,
      onSelect: options.onExplore ? () => options.onExplore?.(folder) : undefined,
    });
    verbs.push({ label: 'Sea&rch...', onSelect: searchCompanion });
  } else if (openWith) {
    verbs.push(openWith);
  }

  return [
    ...verbs,
    separator,
    sendToMenu(selection),
    separator,
    { label: 'Cu&t', disabled: true },
    { label: '&Copy', disabled: true },
    separator,
    { label: 'Create &Shortcut', onSelect: () => readOnlyDisk('shortcut') },
    {
      label: '&Delete',
      // Nothing in the selection can go: the shell's own folders stay put, the
      // same way they refuse to be dragged anywhere.
      disabled: !selection.some(canDelete),
      onSelect: () => confirmDelete(selection),
    },
    { label: 'Rena&me', disabled: true },
    separator,
    { label: 'P&roperties', onSelect: () => showProperties(node) },
  ];
}

/* --------------------------------------------------------------------------
   The empty space in a folder window
   -------------------------------------------------------------------------- */

export interface FolderMenuOptions {
  /** View ▸ — the same radio rows the toolbar's Views button drops down. */
  viewItems?: MenuItem[];
  /** Arrange Icons by ▸ — one row per sort column, already marked. */
  arrangeItems?: MenuItem[];
  onRefresh?(): void;
  /** Properties of the folder on show, when the window knows which it is. */
  onProperties?(): void;
}

/**
 * The background of a folder window: what is left of Explorer's own View menu
 * once you take away the toolbars, plus the two clipboard rows that have never
 * been available on this machine and the New submenu that never will be.
 */
export function folderBackgroundMenu(options: FolderMenuOptions = {}): MenuItem[] {
  const items: MenuItem[] = [];

  if (options.viewItems?.length) items.push({ label: '&View', items: options.viewItems });
  if (options.arrangeItems?.length) {
    items.push({ label: 'Arrange &Icons by', items: options.arrangeItems });
  }

  items.push(
    { label: 'R&efresh', disabled: options.onRefresh === undefined, onSelect: options.onRefresh },
    separator,
    { label: '&Paste', disabled: true },
    { label: 'Paste &Shortcut', disabled: true },
    separator,
    newMenu(),
    separator,
    {
      label: 'P&roperties',
      disabled: options.onProperties === undefined,
      onSelect: options.onProperties,
    },
  );

  return items;
}
